import { create } from 'zustand';
import { tokenStorage } from '../lib/storage';
import { authApi, referralApi, userApi } from '../lib/endpoints';
import type { User } from '@yingai/shared';

interface RegisterPayload {
  target: string;
  password: string;
  confirmPassword: string;
  nickname?: string;
  inviteCode?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  initialized: boolean;
  init: () => Promise<void>;
  login: (target: string, password: string) => Promise<void>;
  register: (data: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  refreshProfile: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  initialized: false,

  init: async () => {
    const token = tokenStorage.access;
    if (!token) {
      set({ initialized: true });
      return;
    }
    try {
      const user = await userApi.me();
      set({ user, isAuthenticated: true, initialized: true });
    } catch {
      tokenStorage.clear();
      set({ user: null, isAuthenticated: false, initialized: true });
    }
  },

  login: async (target, password) => {
    const res = await authApi.login({ target, password });
    tokenStorage.set(res.tokens.accessToken, res.tokens.refreshToken);
    set({ user: res.user, isAuthenticated: true });
  },

  register: async (data) => {
    const res = await authApi.register({
      target: data.target,
      password: data.password,
      confirmPassword: data.confirmPassword,
      nickname: data.nickname,
      inviteCode: data.inviteCode,
    });
    tokenStorage.set(res.tokens.accessToken, res.tokens.refreshToken);
    set({ user: res.user, isAuthenticated: true });
    if (data.inviteCode) {
      try {
        await referralApi.bind(data.inviteCode);
      } catch {
        /* 邀请码无效不影响注册 */
      }
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    tokenStorage.clear();
    set({ user: null, isAuthenticated: false });
  },

  setUser: (user) => set({ user }),
  refreshProfile: async () => {
    const user = await userApi.me();
    set({ user });
  },
}));
